'use client';

import { useMemo } from 'react';
import type { CalendarEvent } from '@/lib/types';
import { uiFb } from '@/lib/i18n';
import { getLocale } from '@/lib/date-format';

interface UpcomingAtEntityProps {
  events: CalendarEvent[];
  entityLabel: string;
  lang?: string;
  /** Maximum chips to render. Default 3. */
  max?: number;
}

/**
 * Inline "Upcoming" strip for academic pages — the single "now & next"
 * orientation surface. Shows the next few upcoming events as glanceable chips
 * directly above the full key-dates table.
 *
 * Previously a *fixed* band under the nav that ate the mobile fold and linked
 * out to /events/ detail pages; now it flows in the page body (below the H1)
 * and stays on-page since the full table is immediately beneath it. (almanak-57rz.3)
 */
export function UpcomingAtEntity({ events, entityLabel, lang = 'en', max = 3 }: UpcomingAtEntityProps) {
  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return events
      .filter((e) => e.dateEnd >= today)
      .sort((a, b) => a.dateStart.localeCompare(b.dateStart))
      .slice(0, max);
  }, [events, max]);

  if (upcoming.length === 0) return null;

  const locale = getLocale(lang);

  return (
    <section
      role="region"
      aria-label={uiFb('ui.upcoming_at_entity', 'Upcoming at {entity}', lang, { entity: entityLabel })}
      className="mx-auto max-w-6xl px-4 pt-4 md:px-8"
    >
      <div className="flex items-center gap-3 overflow-x-auto content-card-elevated px-3 py-2 text-sm">
        <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-muted">
          {uiFb('ui.upcoming_label', 'Upcoming', lang)}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          {upcoming.map((evt) => {
            const dateLabel = new Date(evt.dateStart + 'T00:00:00').toLocaleDateString(locale, {
              month: 'short',
              day: 'numeric',
            });
            const title = (lang !== 'en' && evt.titleLocal) ? evt.titleLocal : evt.title;
            return (
              <span
                key={evt.id}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border-subtle bg-surface-card px-3 py-1 text-text-secondary"
              >
                <span className="tabular-nums text-muted">{dateLabel}</span>
                <span className="font-medium text-text-primary">{title}</span>
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}
